import React from "react";
import { privacyFieldsForSettings, getPrivacy, PRIVACY_DEFAULTS } from "../../lib/memberPrivacy";
import { useAppSettings } from "../../lib/AppSettingsContext";

// Reusable privacy editor — used inside Edit Member > Personal tab and on
// the My Profile page. The component is fully controlled: pass `value`
// (the member's current `privacy` object, or undefined to use defaults) and
// `onChange(updatedPrivacy)` is called any time a toggle is flipped.
//
// `canEdit` defaults to true; pass false to render a read-only summary
// (currently unused — admin always edits, owner always edits their own).
export default function MemberPrivacyEditor({
  value, onChange,
  isAdmin = false,
  canEdit = true,
  testIdPrefix = "member-privacy",
}) {
  const { settings: appSettings } = useAppSettings();
  const visibleFields = privacyFieldsForSettings(appSettings);
  const merged = getPrivacy({ privacy: value || {} });

  const toggle = (k) => {
    if (!canEdit) return;
    const next = { ...merged, [k]: !merged[k] };
    onChange(next);
  };

  const resetAll = () => {
    if (!canEdit) return;
    onChange({ ...PRIVACY_DEFAULTS });
  };

  return (
    <div data-testid={testIdPrefix} className="member-privacy-section">
      <div
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          gap: 12, flexWrap: "wrap", marginBottom: 6,
        }}
      >
        <div className="form-section-title" style={{ margin: 0 }}>Privacy</div>
        {canEdit && (
          <button
            type="button"
            className="btn btn-sm btn-outline"
            data-testid={`${testIdPrefix}-reset`}
            onClick={resetAll}
            title="Restore every field to its default visibility (all visible except Birth Year)."
          >Reset to defaults</button>
        )}
      </div>
      <p style={{
        fontFamily: "'Montserrat', sans-serif", fontSize: 12,
        color: "var(--kc-sub)", lineHeight: 1.45, margin: "0 0 12px",
      }}>
        {isAdmin
          ? "Toggle individual fields off to hide them from everyone except admins and this member's assigned pastor. The member themselves always sees every field."
          : "Hide fields you don't want other members to see. Super Admin, Limited Admin, and your assigned pastor always see every field. Hiding a field removes it completely from other members' view."}
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 8,
        }}
      >
        {visibleFields.map(f => {
          const visible = !!merged[f.key];
          return (
            <label
              key={f.key}
              data-testid={`${testIdPrefix}-row-${f.key}`}
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "8px 10px",
                background: visible ? "color-mix(in srgb, var(--kc-accent) 5%, transparent)" : "rgba(255,138,101,0.07)",
                border: `1px solid ${visible ? "color-mix(in srgb, var(--kc-accent) 20%, transparent)" : "rgba(255,138,101,0.25)"}`,
                borderRadius: 8,
                cursor: canEdit ? "pointer" : "default",
              }}
            >
              <input
                type="checkbox"
                data-testid={`${testIdPrefix}-toggle-${f.key}`}
                checked={visible}
                disabled={!canEdit}
                onChange={() => toggle(f.key)}
                style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0 }}
              />
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={{
                  fontSize: 12, fontWeight: 600, color: "var(--kc-text)",
                  fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.02em",
                }}>{f.label}</span>
                <span style={{
                  fontSize: 10, color: visible ? "rgba(74,222,128,0.85)" : "rgba(255,138,101,0.85)",
                  fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
                  marginTop: 2,
                }}>{visible ? "Visible" : "Hidden"}</span>
                {f.hint && (
                  <span style={{ fontSize: 10.5, color: "var(--kc-meta)", marginTop: 4, lineHeight: 1.4 }}>{f.hint}</span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
